import { useMutation, useQueryClient } from 'react-query'

import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type SubmitCreditCardBillPayload } from '@webapp/modules/accounts-payable/business-logic/interfaces/IReconcileSessionRepository'
import { reconcileSessionRepository } from '@webapp/modules/accounts-payable/data-access/repositories/ReconcileSessionRepository'
import { ReconcileSession } from '@webapp/modules/accounts-payable/entities/ReconcileSession'
import { creditCardAccountQueries } from '@webapp/modules/accounts-payable/query-configs/creditCardAccountQueries'

interface SubmitCreditCardBillVariables {
  sessionId: number
  /** Session detail cache to refresh once the session is SUBMITTED */
  accountId: number
  payload: SubmitCreditCardBillPayload
}

// THE command (CQS). Submits the bill via PUT sessions/{id}/ (status: 1) and
// invalidates the account detail — the session is now SUBMITTED (BHV-049).
export const useMutateSubmitCreditCardBill = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    ReconcileSession,
    RepoError,
    SubmitCreditCardBillVariables
  >({
    mutationFn: async ({ sessionId, payload }) => {
      const updated = await reconcileSessionRepository.submitBill(
        sessionId,
        payload
      )

      return new ReconcileSession(updated)
    },
    onSuccess: (_result, { accountId }) => {
      queryClient.invalidateQueries({
        queryKey: creditCardAccountQueries.detail(accountId).queryKey,
      })
    },
  })

  return {
    submitBill: mutation.mutateAsync,
    isSubmitting: mutation.isLoading,
    error: mutation.error,
  }
}
